export type LeadGenerationProgressPhase = {
  id: string;
  label: string;
  weight: number;
  status: "PENDING" | "ACTIVE" | "COMPLETE";
  detail?: string;
};

export type LeadGenerationProgressSnapshot = {
  version: 1;
  overallPercent: number;
  phases: LeadGenerationProgressPhase[];
  currentAction: string;
  completedCreatives: number;
  totalCreatives: number;
  startedAt: string;
  updatedAt: string;
  estimatedRemainingMs: number | null;
};

const PHASES = [
  ["analyze", "Analyzing your territory", 15],
  ["targeting", "Building campaign targeting", 15],
  ["creatives", "Generating ad creatives", 45],
  ["campaign", "Preparing your campaign", 15],
  ["review", "Submitting for review", 10],
] as const;

export function buildLeadGenerationProgress(input: {
  status: string;
  completedCreatives?: number;
  totalCreatives?: number;
  startedAt?: Date | string;
  previous?: LeadGenerationProgressSnapshot | null;
}): LeadGenerationProgressSnapshot {
  const completedCreatives = input.completedCreatives ?? 0;
  const totalCreatives = input.totalCreatives ?? 3;
  const creativePercent =
    totalCreatives > 0 ? completedCreatives / totalCreatives : 0;
  const percentByStatus: Record<string, number> = {
    DRAFT: 2,
    GENERATING_CREATIVE: 30 + creativePercent * 35,
    PENDING_PAYMENT: 8,
    SETTING_UP: 82,
    IN_REVIEW: 100,
    ACTIVE: 100,
    FAILED: input.previous?.overallPercent ?? 0,
  };
  const calculatedPercent = Math.round(percentByStatus[input.status] ?? 0);
  const overallPercent = Math.max(
    input.previous?.overallPercent ?? 0,
    Math.min(input.status === "IN_REVIEW" || input.status === "ACTIVE" ? 100 : 99, calculatedPercent),
  );
  const activeIndex =
    input.status === "DRAFT"
      ? 0
      : input.status === "GENERATING_CREATIVE"
        ? 2
        : input.status === "PENDING_PAYMENT"
          ? 0
          : 4;
  const now = new Date();
  const startedAt = new Date(input.startedAt ?? now);
  const elapsedMs = Math.max(0, now.getTime() - startedAt.getTime());
  const estimatedRemainingMs =
    overallPercent > 0 && overallPercent < 100
      ? Math.max(0, Math.round((elapsedMs / overallPercent) * (100 - overallPercent)))
      : null;

  return {
    version: 1,
    overallPercent,
    phases: PHASES.map(([id, label, weight], index) => ({
      id,
      label,
      weight,
      status:
        index < activeIndex
          ? "COMPLETE"
          : index === activeIndex
            ? "ACTIVE"
            : "PENDING",
    })),
    currentAction:
      input.status === "PENDING_PAYMENT"
        ? "Waiting for payment"
        : PHASES[Math.min(activeIndex, PHASES.length - 1)]?.[1] ??
          "Preparing campaign",
    completedCreatives,
    totalCreatives,
    startedAt: startedAt.toISOString(),
    updatedAt: now.toISOString(),
    estimatedRemainingMs,
  };
}

export function formatLeadGenerationEta(remainingMs: number | null): string | null {
  if (!remainingMs || remainingMs < 5_000) return null;
  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.round((remainingMs % 60_000) / 1_000);
  return minutes > 0 ? `About ${minutes} min${minutes === 1 ? "" : "s"}` : `About ${seconds}s`;
}
